<template>
  <q-dialog v-model="shown">
    <q-card style="min-width: 400px;">
      <q-form
        ref="formRef"
      >
        <q-card-section class="row items-center q-mx-xs">
          <div class="text-h6">
            Execute Advanced Action
          </div>
          <q-space />
          <q-btn
            v-close-popup
            icon="close"
            flat
            round
            dense
          />
        </q-card-section>
        <q-card-section>
          <div
            class="row q-col-gutter-sm items-start justify-between"
          >
            <q-select
              v-model="developer.action"
              class="col-12"
              label="Action"
              :options="developer.fieldOptions('action')"
              :rules="[val => !!val || 'Selection is required']"
              map-options
              emit-value
              bottom-slots
            />
            <q-input
              v-if="DEVELOPER_INPUT_ACTIONS.DEPLOY_APPSTORE_INSTRUCTIONS.includes(developer.action)"
              class="col-12 q-mb-md"
              autogrow
              readonly
            >
              <template #default>
                <div class="col-12 row text-center q-pa-md">
                  <span
                    class="col-12 text-negative text-h6 q-mb-sm"
                  >
                    READ ME
                  </span>
                  <span
                    v-if="developer.appStore === 'ios'"
                    class="col-12 text-black"
                  >
                    Before deploying to the app store, you must have already created the app via the Apple App Store Connect Portal. Apple does not support creating apps via API keys, so this process cannot be automated.
                    <br><br>Once the new app build has been pushed, manually promote the build to release for review via the Apple App Store Connect Portal.
                  </span>
                  <span
                    v-else
                    class="col-12 text-black"
                  >
                    Google does not support publishing apps first time via their API. To publish the produced bundle, complete the following:
                    <br><br>Navigate to https://github.com/INDY-Cinema-Group/indy-frontend/releases. You can find your completed app as "{{ developer.appStoreBundleIdSuffix }}-app-release-timestamp".
                    <br><br>Download the app file (make sure it's the latest timestamp!), then create and upload the app via Google Play Console.
                  </span>
                </div>
              </template>
            </q-input>
            <q-select
              v-if="DEVELOPER_INPUT_ACTIONS.APP_STORE.includes(developer.action)"
              v-model="developer.appStore"
              class="col-3"
              label="App Store"
              :options="developer.fieldOptions('appStore')"
              :rules="[val => required.$validator(val) || 'Selection is required']"
              map-options
              emit-value
              bottom-slots
            />
            <q-input
              v-if="DEVELOPER_INPUT_ACTIONS.SITE_ID.includes(developer.action)"
              v-model="developer.siteId"
              class="col-3"
              label="Site ID"
              readonly
              :rules="[val => required.$validator(val) || 'Field is required']"
              bottom-slots
            >
              <q-tooltip>
                The ID of the site to be deployed.
              </q-tooltip>
            </q-input>
            <q-input
              v-if="DEVELOPER_INPUT_ACTIONS.APP_VERSION.includes(developer.action) && developer.appStore === 'ios'"
              v-model="developer.appStoreVersion"
              class="col-6"
              label="App Store Version"
              :rules="[
                val => required.$validator(val) || 'Field is required',
                val => /^[0-9]+\.[0-9]+\.[0-9]+$/.test(val) || 'Must be a valid semantic version'
              ]"
              bottom-slots
            >
              <q-tooltip>
                The version of the app in the app store.
              </q-tooltip>
            </q-input>
            <q-input
              v-if="DEVELOPER_INPUT_ACTIONS.APP_STORE_NAME.includes(developer.action)"
              v-model="developer.appStoreName"
              class="col-6"
              label="App Store Name"
              :rules="[
                val => required.$validator(val) || 'Field is required',
                val => /^[a-zA-Z\s]*$/.test(val) || 'Restricted to letters and spaces'
              ]"
              bottom-slots
            >
              <q-tooltip>
                The name of the app in the App Store. Cannot contain special characters.
              </q-tooltip>
            </q-input>
            <q-input
              v-if="DEVELOPER_INPUT_ACTIONS.APP_STORE_BUNDLE_ID_SUFFIX.includes(developer.action)"
              v-model="developer.appStoreBundleIdSuffix"
              class="col-6"
              label="App Store Bundle Id Suffix"
              :rules="[
                val => required.$validator(val) || 'Field is required',
                val => /^[a-z\-]*$/.test(val) || 'Lowercase letters only'
              ]"
              bottom-slots
            >
              <q-tooltip>
                The suffix attached to the package uploaded to the app store - com.indycinemagroup.appStoreBundleIdSuffix.
              </q-tooltip>
            </q-input>
            <q-input
              v-if="DEVELOPER_INPUT_ACTIONS.NETLIFY_URL.includes(developer.action)"
              v-model="developer.netlifyUrl"
              class="col-6"
              label="Netlify Url"
              readonly
              :rules="[val => required.$validator(val) || 'Field is required']"
              bottom-slots
            >
              <q-tooltip>
                The netlify URL of the site to be deployed. This will be used to get site branding settings and is what the published app will display.
              </q-tooltip>
            </q-input>
            <q-input
              v-if="DEVELOPER_INPUT_ACTIONS.IMGIX_HOST.includes(developer.action)"
              v-model="developer.imgixHost"
              class="col-6"
              label="Imgix Host"
              readonly
              :rules="[val => required.$validator(val) || 'Field is required']"
              bottom-slots
            >
              <q-tooltip>
                The imgix host used by the app to generate images.
              </q-tooltip>
            </q-input>
            <q-input
              v-if="DEVELOPER_INPUT_ACTIONS.USER_FIRST_NAME.includes(developer.action) && developer.appStore === 'ios'"
              v-model="developer.userFirstName"
              class="col-6"
              label="User First Name"
              :rules="[val => required.$validator(val) || 'Field is required']"
              bottom-slots
            >
              <q-tooltip>
                The feedback name that will be used by the app store to respond to review requests.
              </q-tooltip>
            </q-input>
            <q-input
              v-if="DEVELOPER_INPUT_ACTIONS.USER_LAST_NAME.includes(developer.action) && developer.appStore === 'ios'"
              v-model="developer.userLastName"
              class="col-6"
              label="User Last Name"
              :rules="[val => required.$validator(val) || 'Field is required']"
              bottom-slots
            >
              <q-tooltip>
                The feedback name that will be used by the app store to respond to review requests.
              </q-tooltip>
            </q-input>
            <q-input
              v-if="DEVELOPER_INPUT_ACTIONS.USER_EMAIL.includes(developer.action) && developer.appStore === 'ios'"
              v-model="developer.userEmail"
              class="col-6"
              label="User Email"
              :rules="[
                val => required.$validator(val) || 'Field is required',
                val => email.$validator(val.toLowerCase()) || $t('form.errors.emailNotValid')
              ]"
              bottom-slots
            >
              <q-tooltip>
                The feedback email that will be used by the app store to respond to review requests.
              </q-tooltip>
            </q-input>
            <q-input
              v-if="DEVELOPER_INPUT_ACTIONS.USER_PHONE.includes(developer.action) && developer.appStore === 'ios'"
              v-model="developer.userPhone"
              class="col-6"
              label="User Phone"
              :rules="[
                val => required.$validator(val) || 'Field is required',
                val => /^[\+0-9\-]+$/.test(val) || 'Must be a valid phone number'
              ]"
              bottom-slots
            >
              <q-tooltip>
                The feedback phone number that will be used by the app store to respond to review requests. Use an international format (e.g. +64...)
              </q-tooltip>
            </q-input>
            <q-input
              v-if="DEVELOPER_INPUT_ACTIONS.DAYS.includes(developer.action)"
              v-model="developer.days"
              class="col"
              label="Days"
              type="number"
              :rules="[val => required.$validator(val) || val > 0 || 'Input must be above 0']"
              bottom-slots
            />
            <q-input
              v-if="DEVELOPER_INPUT_ACTIONS.DAYS.includes(developer.action)"
              v-model="developer.days"
              class="col"
              label="Days"
              type="number"
              :rules="[val => required.$validator(val) || val > 0 || 'Input must be above 0']"
              bottom-slots
            />

            <q-input
              v-if="DEVELOPER_INPUT_ACTIONS.DATE_RANGE.includes(developer.action)"
              :model-value="dateRangeValue"
              style="min-width: 250px;"
              class="col"
              label="Range"
              @click="showCalendar = true"
            >
              <template #append>
                <q-icon
                  name="event"
                  class="cursor-pointer"
                >
                  <q-popup-proxy
                    v-model="showCalendar"
                    transition-show="scale"
                    transition-hide="scale"
                  >
                    <q-date
                      v-model="dateRange"
                      mask="YYYY-MM-DD"
                      range
                    />
                  </q-popup-proxy>
                </q-icon>
              </template>
            </q-input>

            <q-checkbox
              v-if="DEVELOPER_INPUT_ACTIONS.ALL_SITES.includes(developer.action)"
              v-model="developer.allSites"
              class="col-auto"
              size="xl"
              left-label
              label="All Sites"
            />
            <q-select
              v-if="DEVELOPER_INPUT_ACTIONS.LOG_LEVEL.includes(developer.action)"
              v-model="developer.logLevel"
              class="col-12"
              label="Log Level"
              :options="developer.fieldOptions('logLevel')"
              :rules="[val => required.$validator(val) || 'Selection is required']"
              map-options
              emit-value
              bottom-slots
            />
            <q-checkbox
              v-if="DEVELOPER_INPUT_ACTIONS.FORCE.includes(developer.action)"
              v-model="developer.force"
              class="col-auto"
              size="xl"
              left-label
              label="Force"
            />
            <q-select
              v-if="DEVELOPER_INPUT_ACTIONS.YEAR.includes(developer.action)"
              v-model="developer.year"
              class="col-12"
              label="Import Festivals By Year"
              :options="yearOptions"
              :rules="[val => required.$validator(val) || 'Selection is required']"
              map-options
              emit-value
              bottom-slots
            />
            <q-select
              v-if="DEVELOPER_INPUT_ACTIONS.RECOGNITION_TYPES.includes(developer.action)"
              v-model="developer.recognitionType"
              class="col-12"
              label="Recognition Types"
              :options="developer.fieldOptions('recognitionType')"
              map-options
              emit-value
              bottom-slots
            />
            <q-checkbox
              v-if="DEVELOPER_INPUT_ACTIONS.IS_CASHLESS.includes(developer.action)"
              v-model="developer.isCashless"
              class="col-auto"
              size="xl"
              left-label
              label="Is Cashless"
            />
            <q-select
              v-if="DEVELOPER_INPUT_ACTIONS.SCREEN_NAME.includes(developer.action)"
              v-model="developer.screenName"
              class="col-6"
              label="Screen Name"
              :options="$_.map(screens.data, 'name')"
            />
            <q-select
              v-if="DEVELOPER_INPUT_ACTIONS.POSITION.includes(developer.action)"
              v-model="developer.position"
              class="col-6"
              label="Delivery Position"
              :options="deliveryPositions"
            />
            <q-checkbox
              v-if="DEVELOPER_INPUT_ACTIONS.REMOVE_PAGER.includes(developer.action)"
              v-model="developer.removePager"
              class="col-auto"
              size="xl"
              left-label
              label="Remove Pager"
            />
          </div>
        </q-card-section>
        <q-card-section>
          <div
            class="row"
          >
            <q-btn
              size="lg"
              class="col-12"
              color="negative"
              label="Execute"
              :loading="loading"
              @click="execute"
            />
          </div>
        </q-card-section>
      </q-form>
    </q-card>
  </q-dialog>
</template>

<script>
import { email, required } from '@vuelidate/validators'
import { Dialog } from 'quasar'
import { DEVELOPER_ACTIONS, DEVELOPER_INPUT_ACTIONS } from 'src/models/developer/Developer'
import { IsoDate } from 'src/models/model'
import FormValidationMixin from 'src/modules/admin/mixins/FormValidationMixin'

export default {
  name: 'DeveloperAdvancedFunctionsDialog',
  mixins: [FormValidationMixin],
  props: {
    modelValue: {
      type: Boolean,
      required: false
    }
  },
  emits: ['update:model-value'],
  data () {
    return {
      shown: false,
      showCalendar: false,
      dateRange: null,
      loading: false,
      yearOptions: []
    }
  },
  configuration: ['screens', 'deliveryLocations'],
  models: {
    developer: 'default'
  },
  computed: {
    dateRangeValue () {
      if (this.dateRange && this.dateRange.from) {
        return IsoDate.parse(this.dateRange.from).i18n('shortDate') + ' to ' + IsoDate.parse(this.dateRange.to).i18n('shortDate')
      } else {
        return IsoDate.parse(this.dateRange).i18n('shortDate')
      }
    },
    deliveryPositions () {
      if (this.developer.screenName) {
        const resolvedSeats = _.find(this.screens.data, { name: this.developer.screenName })?.seatChart?.availableSeats
        if (resolvedSeats) {
          return resolvedSeats
        }
      }
      return []
    }
  },
  watch: {
    modelValue () {
      this.shown = this.modelValue
    },
    dateRange () {
      if (this.dateRange) {
        this.showCalendar = false
        this.validateDateRange()
      }
    },
    'dateRange.from' () {
      this.validateDateRange()
    },
    'dateRange.to' () {
      this.validateDateRange()
    },
    shown () {
      this.$emit('update:model-value', this.shown)
    }
  },
  async created () {
    _.extend(this, {
      email,
      required,
      DEVELOPER_ACTIONS,
      DEVELOPER_INPUT_ACTIONS
    })
    await this.developer.ensureLoaded()
    this.developer.setAppStoreDefaults()
  },
  mounted () {
    const currentYear = new Date().getFullYear()
    for (let year = currentYear; year <= (currentYear + 5); year++) {
      this.yearOptions.push({ label: year.toString(), value: year })
    }
  },
  methods: {
    validateDateRange () {
      this.developer.startDate = this.dateRange ? (this.dateRange.from || this.dateRange) : null
      this.developer.endDate = this.dateRange ? (this.dateRange.to || this.dateRange) : null
    },
    execute () {
      this.validateForm(async () => {
        this.loading = true

        const { errors, results } = await this.developer.save()

        if (errors && errors.length) {
          this.$mh.showErrors(errors)
          this.loading = false
          return
        }

        this.loading = false
        Dialog.create({
          title: 'Message',
          message: results.actionResponse
        }).onOk(_ => {
          this.shown = false
        })
      })
    }
  }
}

</script>
